import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usersApi, type TenantPortalUser } from '@/api/users';
import { tenantRbacApi, type TenantRoleRow } from '@/api/tenant-rbac';
import { refreshSessionFromServer } from '@/lib/session-sync';
import { useAuthStore } from '@/store';
import { useUIStore } from '@/store/useUIStore';
import { useTenantPermission } from '@/hooks/use-tenant-permission';
import { toast } from 'sonner';
import { btnOutlineNeon, btnPrimaryNeon } from '@/lib/admin-ui';
import { cn } from '@/lib/utils';

const STATUSES = ['ACTIVE', 'INACTIVE', 'SUSPENDED'] as const;

const DEFAULT_ROLE_VALUE = '__default__';

export function TenantSubaccountsPage() {
  const user = useAuthStore((s) => s.user);
  const isLight = useUIStore((s) => s.theme === 'light');
  const lightPrimaryBtn =
    'border border-cyan-500 bg-cyan-600 text-white hover:bg-cyan-500';
  const lightOutlineBtn =
    'border border-slate-300 bg-white text-slate-700 hover:border-cyan-400/45 hover:bg-cyan-50 hover:text-cyan-700';
  const lightFieldClass = 'border-slate-300 bg-white text-slate-800';
  const canManage = useTenantPermission('tenant:user:manage');
  const hasMemberView = useTenantPermission('tenant:user:view');
  /** 仅有「查看成员」无「管理」：列表可见、不可改 */
  const readOnly = hasMemberView && !canManage;

  const [rows, setRows] = useState<TenantPortalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [roleOptions, setRoleOptions] = useState<TenantRoleRow[]>([]);
  const [createRoleId, setCreateRoleId] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      if (canManage) {
        const [userRes, roleRes] = await Promise.all([
          usersApi.list(),
          tenantRbacApi.listRoles(),
        ]);
        setRows(userRes.data);
        setRoleOptions(roleRes.data);
      } else {
        const userRes = await usersApi.list();
        setRows(userRes.data);
        setRoleOptions([]);
      }
    } catch {
      toast.error('加载成员列表失败');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!canManage) return;
    try {
      setCreating(true);
      await usersApi.create({
        email: email.trim(),
        password,
        name: name.trim() || undefined,
      });
      toast.success('已创建子账号');
      setEmail('');
      setPassword('');
      setName('');
      setCreateRoleId('');
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '创建失败';
      toast.error(typeof msg === 'string' ? msg : '创建失败');
    } finally {
      setCreating(false);
    }
  }

  async function updateMemberRole(userId: string, roleId: string) {
    if (!canManage) return;
    try {
      await usersApi.setRole(userId, roleId);
      if (userId === user?.id) await refreshSessionFromServer();
      toast.success(
        userId === user?.id
          ? '角色已更新'
          : '角色已更新，对方账号需刷新或重新登录后生效',
      );
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '更新失败';
      toast.error(typeof msg === 'string' ? msg : '更新失败');
    }
  }

  async function updateStatus(id: string, status: string) {
    if (!canManage) return;
    try {
      await usersApi.update(id, { status });
      if (id === user?.id) await refreshSessionFromServer();
      toast.success('状态已更新');
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '更新失败';
      toast.error(typeof msg === 'string' ? msg : '更新失败');
    }
  }

  async function confirmDelete() {
    if (!deleteId || !canManage) return;
    try {
      await usersApi.delete(deleteId);
      toast.success('已删除');
      setDeleteId(null);
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '删除失败';
      toast.error(typeof msg === 'string' ? msg : '删除失败');
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">成员与账号</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          查看本租户用户；具备 <code className="font-mono text-cyan-300/80">tenant:user:manage</code>{' '}
          时可创建、改状态或删除（受保护账号除外）。
        </p>
      </div>

      {readOnly && (
        <div
          className={cn(
            'rounded-lg border px-4 py-3 text-sm',
            isLight
              ? 'border-fuchsia-300/40 bg-fuchsia-50 text-slate-700'
              : 'border-cyan-500/20 bg-cyan-500/5 text-zinc-300',
          )}
        >
          当前为<strong className={cn(isLight ? 'text-fuchsia-700' : 'text-cyan-200/90')}>只读</strong>
          ：你仅有「查看成员」权限，可浏览列表，不可新建或修改成员。
        </div>
      )}

      {canManage && (
        <Card className={cn(isLight ? 'border-slate-300 bg-white' : 'border-white/10 bg-zinc-900/40')}>
          <CardHeader>
            <CardTitle className="text-lg">新建子账号</CardTitle>
            <CardDescription>
              未选择角色时默认「租户成员」；角色定义见「角色与权限」。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={handleCreate}
              className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
            >
              <div className="space-y-2">
                <Label htmlFor="sub-email">邮箱</Label>
                <Input
                  id="sub-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={cn(isLight ? lightFieldClass : 'border-white/10 bg-zinc-950/60')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sub-pass">初始密码</Label>
                <Input
                  id="sub-pass"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={cn(isLight ? lightFieldClass : 'border-white/10 bg-zinc-950/60')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sub-name">姓名（可选）</Label>
                <Input
                  id="sub-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={cn(isLight ? lightFieldClass : 'border-white/10 bg-zinc-950/60')}
                />
              </div>
              <div className="space-y-2">
                <Label>角色</Label>
                <Select
                  value={createRoleId || DEFAULT_ROLE_VALUE}
                  onValueChange={(v) =>
                    setCreateRoleId(v === DEFAULT_ROLE_VALUE ? '' : v)
                  }
                >
                  <SelectTrigger
                    id="sub-role"
                    className={cn(
                      'h-10',
                      isLight ? lightFieldClass : 'border-white/10 bg-zinc-950/60',
                    )}
                  >
                    <SelectValue placeholder="默认：租户成员" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={DEFAULT_ROLE_VALUE}>
                      默认（租户成员）
                    </SelectItem>
                    {roleOptions.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  type="submit"
                  disabled={creating}
                  className={cn(isLight ? lightPrimaryBtn : btnPrimaryNeon)}
                >
                  {creating ? '提交中…' : '创建'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card className={cn(isLight ? 'border-slate-300 bg-white' : 'border-white/10 bg-zinc-900/40')}>
        <CardHeader>
          <CardTitle className="text-lg">用户列表</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">加载中…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={cn(isLight ? 'border-slate-200 hover:bg-transparent' : 'border-white/10 hover:bg-transparent')}>
                  <TableHead>邮箱</TableHead>
                  <TableHead>姓名</TableHead>
                  <TableHead>角色</TableHead>
                  <TableHead>状态</TableHead>
                  {canManage && <TableHead className="text-right">操作</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const primaryRoleId = row.roles?.[0]?.role.id;
                  const isSelf = row.id === user?.id;
                  return (
                    <TableRow key={row.id} className={cn(isLight ? 'border-slate-200' : 'border-white/10')}>
                      <TableCell className="font-medium">{row.email}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>
                        {canManage ? (
                          <Select
                            value={primaryRoleId ?? ''}
                            onValueChange={(v) => updateMemberRole(row.id, v)}
                            disabled={isSelf || row.isProtected}
                          >
                            <SelectTrigger
                              className={cn(
                                'h-8 min-w-[140px]',
                                isLight ? lightFieldClass : 'border-white/10 bg-zinc-950/60',
                              )}
                            >
                              <SelectValue placeholder="角色" />
                            </SelectTrigger>
                            <SelectContent>
                              {roleOptions.map((r) => (
                                <SelectItem key={r.id} value={r.id}>
                                  {r.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-muted-foreground">
                            {row.roles?.map((r) => r.role.name).join('、') ?? '—'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {canManage ? (
                          <Select
                            value={row.status}
                            onValueChange={(v) => updateStatus(row.id, v)}
                            disabled={isSelf && row.status === 'ACTIVE'}
                          >
                            <SelectTrigger
                              className={cn(
                                'h-8 w-[140px]',
                                isLight ? lightFieldClass : 'border-white/10 bg-zinc-950/60',
                              )}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>
                                  {s}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          row.status
                        )}
                      </TableCell>
                      {canManage && (
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={cn(isLight ? lightOutlineBtn : btnOutlineNeon)}
                            disabled={isSelf || row.isProtected}
                            onClick={() => setDeleteId(row.id)}
                          >
                            删除
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent
          className={cn(
            isLight
              ? 'border-slate-300 bg-white text-slate-900'
              : 'border-white/10 bg-zinc-950 text-foreground',
          )}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除该用户？</AlertDialogTitle>
            <AlertDialogDescription className={cn(isLight ? 'text-slate-500' : 'text-zinc-400')}>
              此操作不可恢复。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={cn(isLight ? lightOutlineBtn : btnOutlineNeon)}>
              取消
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(isLight ? lightPrimaryBtn : btnPrimaryNeon)}
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
